import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Calendar, Trophy, Play } from 'lucide-react';
import { fetchMyEvents, friendlyError } from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { toast } from 'sonner';
import { SectionTitle } from '../SectionTitle';
import { EmptyState } from '../EmptyState';
import { AccessBadge } from '../AccessBadge';

export function MyEventsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchMyEvents();
        if (alive) setItems(data.items || []);
      } catch (err) {
        if (alive) toast.error(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
      <SectionTitle
        icon={Trophy}
        label="Mis partidos"
        hint="Partidos a los que ya tienes acceso."
      />
      {loading ? (
        <div className="grid sm:grid-cols-2 gap-3">{[1, 2].map((i) => (
          <Skeleton key={i} className="h-28 w-full rounded-xl" />
        ))}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title="Aún no hay partidos en tu lista"
          description="Cuando compres un partido o tengas suscripción con acceso, aparecerá aquí."
          ctaLabel="Ver partidos disponibles"
          ctaTo="/eventos"
          testId={DASHBOARD.eventsEmpty}
        />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
          {items.map((ev) => (
            <div
              key={ev.id}
              data-testid={DASHBOARD.eventRow(ev.id)}
              className="flex gap-3 p-3 rounded-xl border border-[var(--ns-border)] hover:bg-[var(--ns-bg-2)]/40 transition-colors"
            >
              <div className="h-20 w-28 rounded-lg overflow-hidden bg-[var(--ns-bg-2)] shrink-0">
                {ev.thumbnail_url ? (
                  <img src={ev.thumbnail_url} alt={ev.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full inline-flex items-center justify-center text-[var(--ns-gold-700)]">
                    <Trophy className="h-6 w-6" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                <p className="text-sm font-semibold text-[var(--ns-text)] line-clamp-2">{ev.title}</p>
                <p className="text-xs text-[var(--ns-text-muted)] flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {ev.event_date ? new Date(ev.event_date).toLocaleDateString('es-ES') : '—'}
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-auto">
                  <AccessBadge via={ev.access_via} className="text-[10px]" />
                  {ev.slug ? (
                    <Button asChild size="sm" className="h-7 px-2 rounded-lg bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/25 border-0">
                      <Link to={`/eventos/${ev.slug}/watch`} className="inline-flex items-center gap-1 text-xs">
                        <Play className="h-3 w-3" />
                        Ver
                      </Link>
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
